// src/components/sections/FAQ/Faq.jsx
import React, { useRef, useState, useEffect } from "react";
import "./faq.css";
import Loader from "../../elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";

function Faq() {
  const [openId, setOpenId] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const refs = useRef({});
  const offset = 90;

  const faqData = useStore(state => state.faq);
  const loading = useStore(state => state.loading);
  const error = useStore(state => state.error);
  const fetchFaq = useStore(state => state.fetchFaq);
  const showToast = useToast();

  // 1) Cargar FAQs al montar
  useEffect(() => {
    fetchFaq();
  }, [fetchFaq]);

  // 2) Mostrar toast en caso de error
  useEffect(() => {
    if (error) {
      showToast("error", `❌ Error al cargar preguntas frecuentes: ${error}`);
    }
  }, [error, showToast]);

  // 3) Derivar el array de FAQs desde el store
  useEffect(() => {
    if (!Array.isArray(faqData) || faqData.length === 0) {
      setFaqs([]);
      return;
    }

    // Extraer el contenido de cada item
    const allfaqs = faqData.flatMap(item =>
      Array.isArray(item.preguntasFrecuentesContent)
        ? item.preguntasFrecuentesContent
        : []
    );

    // Parsear las respuestas JSON
    const parsedFaqs = allfaqs.map(faq => ({
      ...faq,
      respuestas: parseJSONRespuestas(faq.respuestas)
    }));

    setFaqs(parsedFaqs);
  }, [faqData]);

  // 4) Toggle y scroll
  const toggle = id => {
    setOpenId(prev => (prev === id ? null : id));
    setTimeout(() => {
      const el = refs.current[id];
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  // 5) Helpers de parsing
  const parseJSONRespuestas = jsonString => {
    try {
      const lines = JSON.parse(`[${jsonString}]`);
      return lines.flat();
    } catch (err) {
      console.warn("⚠️ Error parseando respuestas:", err);
      return [];
    }
  };

  const parseRespuestas = respuestas => {
    const result = [];
    let current = null;

    respuestas.forEach(linea => {
      const text = typeof linea === "string" ? linea : String(linea);

      if (text.trim().startsWith(">")) {
        if (current) {
          current.subitems = current.subitems || [];
          current.subitems.push(text.replace(/^>\s?/, "").trim());
        }
      } else {
        if (current) result.push(current);
        current = { texto: text, subitems: [] };
      }
    });

    if (current) result.push(current);
    return result;
  };

  // 6) Loader
  if (loading) return <Loader />;
  if (faqs.length === 0) return null;

  // 7) Render
  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <div className="faq-container space-y-4">
            {faqs.map(({ id, pregunta, respuestas }) => (
              <div
                key={id}
                className="faq-item border rounded-md p-4"
                ref={el => (refs.current[id] = el)}
              >
                <button
                  onClick={() => toggle(id)}
                  className="w-full text-left font-semibold text-lg"
                >
                  {pregunta}
                </button>

                {openId === id && (
                  <ul className="faq-answer mt-2 list-disc pl-5 text-gray-700">
                    {parseRespuestas(respuestas).map((resp, idx) => (
                      <li key={idx}>
                        <span
                          dangerouslySetInnerHTML={{ __html: resp.texto }}
                        />
                        {resp.subitems.length > 0 && (
                          <ul className="list-disc pl-5 mt-1">
                            {resp.subitems.map((sub, sidx) => (
                              <li
                                key={sidx}
                                dangerouslySetInnerHTML={{ __html: sub }}
                              />
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Faq;
